import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { feedbackSettingsService } from "@/services/feedback-settings-service";
import {
  CommunicationFeedbackSettings,
  RequestFeedbackSettings,
  UpdateCommunicationFeedbackSettingsPayload,
} from "@/types/feedback-settings";
import { is404 } from "@/utils/httpErrors";

export const feedbackSettingsKeys = {
  all: ["feedback-settings"] as const,
  requests: () => [...feedbackSettingsKeys.all, "requests"] as const,
  communication: () => [...feedbackSettingsKeys.all, "communication"] as const,
};

export const useRequestFeedbackSettings = (hotelId?: string) =>
  useQuery<RequestFeedbackSettings>({
    queryKey: feedbackSettingsKeys.requests(),
    enabled: !!hotelId,
    queryFn: async () => {
      try {
        return await feedbackSettingsService.getRequestFeedbackSettings();
      } catch (error) {
        if (is404(error)) {
          return {
            enabled: false,
            allowComment: false,
          };
        }

        throw error;
      }
    },
  });

export const useUpdateRequestFeedbackSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RequestFeedbackSettings) =>
      feedbackSettingsService.updateRequestFeedbackSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: feedbackSettingsKeys.requests(),
      });
    },
  });
};

export const useCommunicationFeedbackSettings = (hotelId?: string) =>
  useQuery<CommunicationFeedbackSettings>({
    queryKey: feedbackSettingsKeys.communication(),
    enabled: !!hotelId,
    queryFn: async () => {
      try {
        return await feedbackSettingsService.getCommunicationFeedbackSettings();
      } catch (error) {
        if (is404(error)) {
          return {
            enabled: false,
            question: "Please rate the communication",
            responseType: "STARS",
            allowComment: false,
            followUps: [],
          };
        }

        throw error;
      }
    },
  });

export const useUpdateCommunicationFeedbackSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateCommunicationFeedbackSettingsPayload) =>
      feedbackSettingsService.updateCommunicationFeedbackSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: feedbackSettingsKeys.communication(),
      });
    },
  });
};
